import { apiClient } from '@/api/client';
import { Business } from '@/api/models';
import { useEntityDrawer } from '@/hooks/useEntityDrawer';
import { queryKeys } from '@/lib/queryKeys';
import { BusinessFormData } from '@/pages/businesses/hooks/useBusinessMutations';
import * as React from 'react';
import { BusinessDrawerView } from './BusinessDrawerView';

export interface BusinessDrawerProps {
  onFormSubmit: (formData: BusinessFormData) => Promise<void>;
  onDeleteBusiness: (business: Business) => void;
  onToggleReview: (
    businessId: number,
    reviewId: number,
    isEvaluated: boolean,
  ) => void;
  onDeleteImage: (businessId: number, imageId: number) => void;
  onSetPrimaryImage: (businessId: number, imageId: number) => void;
  isFormLoading: boolean;
  isTogglingReview: boolean;
  isDeletingImage: boolean;
  isSettingPrimaryImage: boolean;
}

export const BusinessDrawer: React.FC<BusinessDrawerProps> = ({
  onFormSubmit,
  onDeleteBusiness,
  onToggleReview,
  onDeleteImage,
  onSetPrimaryImage,
  isFormLoading,
  isTogglingReview,
  isDeletingImage,
  isSettingPrimaryImage,
}) => {
  const { state, actions, isLoading } = useEntityDrawer<Business>({
    basePath: '/businesses',
    queryKey: queryKeys.ADMIN_BUSINESS,
    fetchFn: async (id: number) => {
      const response = await apiClient.GET('/admin/businesses/{id}', {
        params: { path: { id } },
      });
      if (response.error) throw new Error('Failed to fetch business');
      return response.data as Business;
    },
    supportsCreate: false,
  });

  return (
    <BusinessDrawerView
      isOpen={state.isOpen}
      business={state.entity}
      mode={state.mode as 'view' | 'edit'}
      isLoading={isLoading}
      isFormLoading={isFormLoading}
      isTogglingReview={isTogglingReview}
      isDeletingImage={isDeletingImage}
      isSettingPrimaryImage={isSettingPrimaryImage}
      onClose={actions.close}
      onEdit={business => actions.edit(business.id!)}
      onDelete={onDeleteBusiness}
      onToggleReview={onToggleReview}
      onFormSubmit={onFormSubmit}
      onDeleteImage={onDeleteImage}
      onSetPrimaryImage={onSetPrimaryImage}
    />
  );
};
